import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { articles, articlesList } from "@/mocks/articles";

function getArticleSchema(article: typeof articles[0]) {
  const siteUrl = "https://hoobuy.lifestyle";
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.thumbnail,
    "author": {
      "@type": "Organization",
      "name": "Equipe Hoobuy Lifestyle",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "Hoobuy Lifestyle",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg"
      }
    },
    "datePublished": article.date,
    "dateModified": article.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artigo/${article.slug}`
    },
    "inLanguage": "pt-BR"
  };
}

function getBreadcrumbSchema(article: typeof articles[0]) {
  const siteUrl = "https://hoobuy.lifestyle";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Início",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Artigos",
        "item": `${siteUrl}/artigos`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title
      }
    ]
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="w-full max-w-[900px] mx-auto px-4 pt-32 pb-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
          <p className="text-foreground-500 mb-6">O artigo que você procura não está disponível.</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar ao Início
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articlesList
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getArticleSchema(article)) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getBreadcrumbSchema(article)) }}
        />
        <article className="w-full max-w-[900px] mx-auto px-4 md:px-6 lg:px-10 pt-28 pb-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-foreground-400 mb-8" aria-label="Breadcrumb">
            <a href="/" className="hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Início</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600 whitespace-nowrap">Artigos</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium truncate max-w-[250px]">{article.title}</span>
          </nav>

          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                {article.category}
              </span>
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">
                Atualizado {new Date(article.date).toLocaleDateString("pt-BR")}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.15] mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed max-w-3xl">
              {article.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950 leading-tight">{t("article.author")}</p>
                  <p className="text-xs text-foreground-400 leading-tight">{t("article.updated")}: {article.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-400">
                <i className="ri-check-double-line text-accent-500"></i>
                <span>{t("article.reviewed")}</span>
              </div>
            </div>
          </header>

          <div className="prose-custom text-foreground-700 leading-relaxed space-y-6">
            {article.sections.map((section, i) => {
              if (section.type === "heading") {
                return (
                  <h2 key={i} className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
                    {section.content}
                  </h2>
                );
              }
              return (
                <p key={i} dangerouslySetInnerHTML={{ __html: section.content }} className="text-base leading-relaxed" />
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3 mt-12">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              Acessar Hoobuy Sheet
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-950 bg-background-100 hover:bg-background-200/70 rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              {t("article.back_home")}
            </a>
          </div>

          <footer className="mt-16 pt-8 border-t border-background-200/70">
            <h3 className="font-heading text-xl font-bold text-foreground-950 mb-4">{t("article.related")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <a
                  key={rel.slug}
                  href={`/artigo/${rel.slug}`}
                  className="block p-5 bg-background-100 rounded-xl hover:bg-background-100/70 transition-all cursor-pointer group"
                >
                  <p className="text-sm font-semibold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">{rel.title}</p>
                  <p className="text-xs text-foreground-400 line-clamp-2">{rel.excerpt}</p>
                </a>
              ))}
            </div>
          </footer>
        </article>
      </main>

      <Footer />
    </div>
  );
}